import {
    ChangeDetectorRef,
    Component,
    forwardRef,
    Input,
    OnChanges,
    SimpleChanges
} from '@angular/core';
import {ControlValueAccessor, NG_VALUE_ACCESSOR} from '@angular/forms';
import {ConfiguratorDatastoreService} from '@tj/tj-dmp-lib-api';
import {SelectItem} from 'primeng/api';

@Component({
    selector: 'dmp-partner-select-or-free-input',
    templateUrl: './partner-select-or-free-input.component.html',
    styleUrls: ['./partner-select-or-free-input.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            multi: true,
            useExisting: forwardRef(() => PartnerSelectOrFreeInputComponent),
        },
    ],
    standalone: false
})
export class PartnerSelectOrFreeInputComponent implements ControlValueAccessor, OnChanges {

    @Input() datastore: string;

    value: string | null = null;
    manualValue = '';
    manualMode = false;
    disabled = false;
    partnersLoaded = false;
    partners: SelectItem[] = [];

    private touched = false;
    private manualModeChosenByUser = false;

    constructor(
        private readonly datastoreService: ConfiguratorDatastoreService,
        private readonly changeDetectorRef: ChangeDetectorRef
    ) {
    }

    get selectedPartnerNotFound(): boolean {
        return this.partnersLoaded && !!this.value && !this.partnerExists(this.value);
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.datastore) {
            this.loadPartners();
        }
    }

    writeValue(value: string | null): void {
        this.value = value || null;
        this.manualValue = value || '';
        this.syncModeWithValue();
        this.changeDetectorRef.detectChanges();
    }

    registerOnChange(onChange: (value: string | null) => void): void {
        this.onChange = onChange;
    }

    registerOnTouched(onTouched: () => void): void {
        this.onTouched = onTouched;
    }

    setDisabledState(disabled: boolean): void {
        this.disabled = disabled;
    }

    onPartnerSelected(value: string | string[] | null): void {
        if (this.disabled) {
            return;
        }
        const selectedValue = Array.isArray(value) ? value[0] : value;
        this.setValue(selectedValue || null);
    }

    onManualInput(value: string): void {
        if (this.disabled) {
            return;
        }
        this.manualValue = value;
        this.setValue(value?.trim() || null, false);
    }

    onManualModeChange(manualMode: boolean): void {
        if (manualMode) {
            this.switchToManual();
        } else {
            this.switchToSelect();
        }
    }

    switchToManual(): void {
        if (this.disabled) {
            return;
        }
        this.manualModeChosenByUser = true;
        this.manualMode = true;
        this.manualValue = this.value || '';
        this.markAsTouched();
    }

    switchToSelect(): void {
        if (this.disabled) {
            return;
        }
        this.manualModeChosenByUser = false;
        this.manualMode = false;
        if (this.value && !this.partnerExists(this.value)) {
            this.setValue(null);
        }
        this.markAsTouched();
    }

    markAsTouched(): void {
        if (!this.touched) {
            this.onTouched();
            this.touched = true;
        }
    }

    private loadPartners(): void {
        this.partners = [];
        this.partnersLoaded = false;

        if (!this.datastore) {
            this.partnersLoaded = true;
            this.syncModeWithValue();
            return;
        }

        this.datastoreService.getDatastoreUsingGET(this.datastore, true).subscribe(datastore => {
            this.partners = (datastore.partners || []).map(partner => ({
                label: `${partner.name} [${partner.code}]`,
                value: partner.code
            }));
            this.partnersLoaded = true;
            this.syncModeWithValue();
        });
    }

    private syncModeWithValue(): void {
        if (!this.partnersLoaded) {
            return;
        }
        if (this.manualModeChosenByUser) {
            return;
        }
        this.manualMode = !!this.value && !this.partnerExists(this.value);
    }

    private partnerExists(value: string | null): boolean {
        return !!value && this.partners.some(partner => partner.value === value);
    }

    private setValue(value: string | null, syncManualValue = true): void {
        this.value = value;
        if (syncManualValue) {
            this.manualValue = value || '';
        }
        this.onChange(value);
        this.markAsTouched();
    }

    private onChange = (_value: string | null) => {
        //
    };

    private onTouched = () => {
        //
    };
}
